import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Rocket, RefreshCcw, CheckCircle2, XCircle, AlertTriangle, ExternalLink } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function ScoreRing({ score, size = 96 }) {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (score / 100) * c;
  const tone = score >= 100 ? "#10b981" : score >= 80 ? "#22d3ee" : score >= 50 ? "#f59e0b" : "#f43f5e";
  return (
    <svg width={size} height={size} className="rotate-[-90deg]">
      <circle cx={size/2} cy={size/2} r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="8" fill="none" />
      <circle cx={size/2} cy={size/2} r={r} stroke={tone} strokeWidth="8" fill="none"
              strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" />
      <text x="50%" y="50%" textAnchor="middle" dy="0.35em" fill="#fff" fontSize="20" fontWeight="900" transform={`rotate(90 ${size/2} ${size/2})`}>{score}</text>
    </svg>
  );
}

function SummaryCard({ label, value, tone = "slate", testid }) {
  const T = { slate: "border-white/10 bg-white/5", emerald: "border-emerald-500/30 bg-emerald-500/10",
              rose: "border-rose-500/30 bg-rose-500/10", amber: "border-amber-500/30 bg-amber-500/10",
              cyan: "border-cyan-500/30 bg-cyan-500/10" };
  return (
    <div className={`rounded-2xl border p-4 ${T[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">{label}</div>
      <div className="text-2xl font-black text-white mt-1">{value}</div>
    </div>
  );
}

function ChecklistItem({ check, onNavigate }) {
  const cls = check.ok ? "border-emerald-500/30 bg-emerald-500/5" :
              check.severity === "WARNING" ? "border-amber-500/30 bg-amber-500/5" :
              "border-rose-500/30 bg-rose-500/5";
  const Icon = check.ok ? CheckCircle2 : (check.severity === "WARNING" ? AlertTriangle : XCircle);
  const iconCls = check.ok ? "text-emerald-400" : (check.severity === "WARNING" ? "text-amber-400" : "text-rose-400");
  return (
    <div className={`rounded-xl border p-3 flex items-center gap-3 ${cls}`} data-testid={`gl-check-${check.name}`}>
      <Icon size={16} className={iconCls} />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-black uppercase tracking-widest text-white">{check.name.replace(/_/g, " ")}</div>
        <div className="text-[10px] font-mono text-white/60 truncate">{check.detail} · {check.engine}</div>
      </div>
      {check.action_route && (
        <Button size="sm" variant="ghost" onClick={() => onNavigate(check.action_route)}
                className="text-white/60 hover:text-white hover:bg-white/10 text-xs" data-testid={`gl-goto-${check.name}`}>
          <ExternalLink size={11} className="mr-1" /> Open
        </Button>
      )}
    </div>
  );
}

function DetailPanel({ tid, onClose, onNavigate }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get(`/platform/go-live/${tid}`); setD(r.data); }
    catch (e) { notifyError(e); }
    finally { setBusy(false); }
  }, [tid]);
  const refresh = async () => {
    setBusy(true);
    try { const r = await api.post(`/platform/go-live/${tid}/refresh`); setD(r.data); toast.success("Refreshed"); }
    catch (e) { notifyError(e); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="text-white/40 text-sm">Memuat…</div>;

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5 mt-4" data-testid={`gl-detail-${tid}`}>
      <div className="flex items-center gap-4 flex-wrap mb-4">
        <ScoreRing score={d.score} />
        <div className="flex-1 min-w-0">
          <div className="text-white font-display font-black text-lg">{d.tenant_name}</div>
          <div className="text-xs text-white/50 font-mono">{d.tenant_slug}</div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${d.production_ready ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-rose-500/15 text-rose-300 border-rose-500/30"}`} data-testid="gl-ready-badge">
              {d.production_ready ? "PRODUCTION READY" : "NOT READY"}
            </span>
            <span className="text-xs text-white/60">{d.passed}/{d.total} checks passed</span>
            <span className="text-xs text-white/60">· ETA: {d.estimated_go_live_time}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={refresh} disabled={busy} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="gl-refresh">
            <RefreshCcw size={12} className="mr-1" /> Refresh
          </Button>
          <Button variant="ghost" onClick={onClose} className="text-white/60 hover:text-white hover:bg-white/10">Close</Button>
        </div>
      </div>

      {d.production_ready && (
        <div className="rounded-2xl border border-emerald-500/40 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 p-4 mb-4 flex items-center gap-3" data-testid="gl-one-click-launch">
          <Rocket size={24} className="text-emerald-300" />
          <div className="flex-1">
            <div className="text-emerald-200 font-black">Ready For One Click Launch</div>
            <div className="text-xs text-emerald-100/70">Semua 14 check hijau — tenant siap Go Live.</div>
          </div>
          <Button className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:opacity-90 text-white font-black" data-testid="gl-launch-btn" onClick={() => onNavigate(`/platform/launch?tenant_id=${d.tenant_id}`)}>
            🚀 Launch
          </Button>
        </div>
      )}

      {d.blocking_issues.length > 0 && (
        <div className="mb-3 rounded-xl border border-rose-500/30 bg-rose-500/5 p-3" data-testid="gl-blocking-list">
          <div className="text-[10px] font-black uppercase tracking-widest text-rose-300 mb-2">Blocking Issues ({d.blocking_issues.length})</div>
          {d.blocking_issues.map(b => <div key={b.name} className="text-xs text-rose-200">✗ {b.name} — {b.detail}</div>)}
        </div>
      )}

      <div className="grid gap-2">
        {d.checks.map(c => <ChecklistItem key={c.name} check={c} onNavigate={onNavigate} />)}
      </div>

      <div className="mt-3 text-[10px] text-white/40 font-mono">
        Verified at {d.verified_at} · duration {d.verification_duration_seconds?.toFixed(2)}s
      </div>
    </div>
  );
}

export default function PlatformGoLive() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/platform/go-live", { params: { limit: 100 } }); setData(r.data); }
    catch (e) { notifyError(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <PlatformLayout title="Go Live Checklist">
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <div className="text-white font-display font-black text-xl">Production Readiness Dashboard</div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" onClick={load} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="gl-overview-refresh">
            <RefreshCcw size={12} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {data && (
        <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-6 mb-4" data-testid="gl-summary">
          <SummaryCard label="Total Tenants" value={data.summary.total_tenants} tone="cyan" testid="gl-sum-total" />
          <SummaryCard label="Ready" value={data.summary.ready} tone="emerald" testid="gl-sum-ready" />
          <SummaryCard label="Blocked" value={data.summary.blocked} tone="rose" testid="gl-sum-blocked" />
          <SummaryCard label="Warning" value={data.summary.warning} tone="amber" testid="gl-sum-warning" />
          <SummaryCard label="Pending" value={data.summary.pending} tone="slate" testid="gl-sum-pending" />
          <SummaryCard label="Avg Score" value={`${data.summary.average_score}`} tone="cyan" testid="gl-sum-avg" />
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
        <table className="w-full text-sm" data-testid="gl-table">
          <thead className="bg-white/5 border-b border-white/10">
            <tr className="text-left text-[10px] uppercase tracking-widest text-white/50">
              <th className="px-4 py-3 font-bold">Tenant</th>
              <th className="px-4 py-3 font-bold">Score</th>
              <th className="px-4 py-3 font-bold">Status</th>
              <th className="px-4 py-3 font-bold">Blocking</th>
              <th className="px-4 py-3 font-bold">Last Check</th>
              <th className="px-4 py-3 font-bold text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-6 text-white/40 text-center">Memuat…</td></tr>}
            {!loading && (data?.items || []).length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-white/40 text-center" data-testid="gl-empty">Belum ada tenant.</td></tr>}
            {!loading && (data?.items || []).map(it => (
              <tr key={it.tenant_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`gl-row-${it.tenant_id}`}>
                <td className="px-4 py-3 text-white/80">{it.tenant_name}</td>
                <td className="px-4 py-3 font-mono text-white">{it.score}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${it.production_ready ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : it.blocking_count > 0 ? "bg-rose-500/15 text-rose-300 border-rose-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30"}`}>
                    {it.production_ready ? "READY" : it.blocking_count > 0 ? "BLOCKED" : "PENDING"}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-white/60 font-mono">{it.first_blocking || "—"}</td>
                <td className="px-4 py-3 text-white/50 text-xs">{it.last_check?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setSelected(it.tenant_id)} className="text-white/60 hover:bg-white/10" data-testid={`gl-view-${it.tenant_id}`}>
                    <ExternalLink size={11} className="mr-1" /> Detail
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <DetailPanel tid={selected} onClose={() => setSelected(null)} onNavigate={(r) => nav(r)} />}
    </PlatformLayout>
  );
}
